/**
 * useSiteMapHealth Hook
 *
 * Polls fleet health for the site map, using the same polling rules as useSiteReliability:
 * - polls 30 s after each response (the backend reuses one snapshot for 30 s, so faster
 *   polling returns the same data)
 * - pauses while the tab is hidden and polls immediately when it becomes visible
 * - never overlaps requests
 * - keeps the last good result when a poll fails
 */
import { useEffect, useReducer, useRef } from 'react';
import { fetchSiteMapHealth } from '../api/siteMapApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import {
  healthReducer,
  healthResultToAction,
  initialHealthState,
  type HealthState,
} from './siteMapState';

export const SITE_MAP_HEALTH_POLL_INTERVAL_MS = 30_000;

export function useSiteMapHealth(): HealthState {
  const [state, dispatch] = useReducer(healthReducer, initialHealthState);
  const { getAccessToken } = useAuth();

  // A ref so a new getAccessToken identity does not restart polling
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  useEffect(() => {
    // Mutable bookkeeping is scoped to this effect run and never shared
    let cancelled = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stopTimer = (): void => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    const scheduleNext = (): void => {
      stopTimer();
      if (!cancelled && !document.hidden) {
        timer = setTimeout(() => void poll(), SITE_MAP_HEALTH_POLL_INTERVAL_MS);
      }
    };

    const poll = async (): Promise<void> => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const result = await fetchSiteMapHealth({
          apiHost: getAppConfig().apiHost,
          getAccessToken: getAccessTokenRef.current,
        });
        if (!cancelled) dispatch(healthResultToAction(result, Date.now()));
      } catch (err) {
        if (!cancelled) {
          dispatch({
            type: 'failed',
            message: err instanceof Error ? err.message : 'Failed to load site health',
          });
        }
      } finally {
        inFlight = false;
        scheduleNext();
      }
    };

    const onVisibilityChange = (): void => {
      stopTimer();
      if (!document.hidden) void poll();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void poll();

    return () => {
      cancelled = true;
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return state;
}
